import {DrawText} from '@platform/ui/DrawReference';
import {drawNumber} from '@platform/contracts/drawNumber';
import {useEffect,useState} from 'react';
import type {Snapshot} from '@platform/contracts/x';
import type {PurchaseIntent} from '@platform/client/purchases';
import {money} from '@platform/client';
import {useCopy} from '@platform/ui/config';
import {BottomCta} from './BottomCta';
import {ActionButton} from './ActionButton';
import {CardBadge} from './CardBadge';
import {RoomNotice} from './RoomNotice';
import './SelectionTray.css';

// A submitted command is a receipt, not an editable draft. Cache only its one
// target; a restored historical receipt may read that draw once, never poll it.
export function PurchaseReceiptTray({intent,draws,busy,processing,connected,summary,onRetry,onReturn,embedded=false}:{
 intent:PurchaseIntent;draws:(Snapshot|null|undefined)[];busy:boolean;processing:boolean;connected:boolean;summary:string;onRetry:()=>void;onReturn?:()=>void;embedded?:boolean;
}){
 const t=useCopy(),{drawId,cardIds=[],amountMinor}=intent.body;
 const [saved,setSaved]=useState<Snapshot|null>(null),[failed,setFailed]=useState(false);
 const known=saved?.drawId===drawId?saved:draws.find(draw=>draw?.drawId===drawId)??null;
 const normalPending=processing||busy&&!summary;
 useEffect(()=>{
  if(known){if(saved?.drawId!==drawId)setSaved(known);setFailed(false);return;}
  const abort=new AbortController();setFailed(false);
  void fetch('/api/tombala/draws/'+encodeURIComponent(drawId),{signal:abort.signal}).then(async response=>{
   if(!response.ok)throw Error('Receipt draw unavailable');
   const draw=await response.json() as Snapshot;
   if(draw.drawId!==drawId)throw Error('Receipt draw mismatch');
   if(!abort.signal.aborted)setSaved(draw);
  }).catch(()=>{if(!abort.signal.aborted)setFailed(true);});
  return()=>abort.abort();
 },[drawId,known?.drawId]);
 return <BottomCta className={'selection-tray'+(embedded?' purchase-panel-footer':'')} revealKey={intent.body.requestId} data-ui="purchase-receipt" expansion={<div className="selection-form">
  <div className="selection-heading"><div className="selection-count"><strong>{t('purchase.receiptCards',{count:cardIds.length})}</strong><small data-ui="purchase-draw"><DrawText copyKey="flow.transactionDraw" draw={drawNumber({drawId,drawNumber:known?.drawNumber??intent.drawNumber})}/></small></div></div>
  <div className="selection-card-strip" tabIndex={0} role="group" aria-label={t('selection.title')}>{cardIds.map(id=><CardBadge key={id} card={known?.cards.find(card=>card.id===id)??{id}}/>)}</div>
  {known&&<div className="selection-count"><small>{t('selection.perCard')} · {money(amountMinor,known.currency)}</small></div>}
  <RoomNotice noticeKey={normalPending?'purchase.processing':'purchase.pending'} dismissKey={intent.body.requestId} tone={normalPending?'info':'warning'}>{summary||t(normalPending?'receipt.pending':'purchase.pending',{count:cardIds.length})}</RoomNotice>
  {failed&&<RoomNotice noticeKey="purchase.receiptDetailsUnavailable" tone="warning">{t('purchase.receiptDetailsUnavailable')}</RoomNotice>}
 </div>}><div className="selection-payment-row" data-return={!!onReturn}>
  {onReturn&&<ActionButton direction="back" data-action="return-to-draw" onClick={onReturn}>{t('flow.returnToDraw')}</ActionButton>}
  <div className="selection-total"><small>{t('selection.total')}</small><strong>{known?money(amountMinor*cardIds.length,known.currency):'—'}</strong></div>
  <ActionButton direction="retry" busy={busy||processing} disabled={!connected||busy||processing} onClick={onRetry}>{t(processing?'purchase.processing':busy?'purchase.checking':'purchase.retry')}</ActionButton>
 </div></BottomCta>;
}
